import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import usePermissions from '../../../../../hooks/usePermissions'

import { isTeamMemberInApprovers } from '../../helpers'
import { insertIf } from '../../../../../helpers/common'

import { updateMemberRole, updateMemberStatus } from '../../../../../modules/actions/team'
import { clearUpdateSelfAccount, updateSelfAccount } from '../../../../../modules/actions/selfAccounts'
import { updatedMemberSelector } from '../../../../../modules/selectors/team'
import { getCurrentUserIDSelector } from '../../../../../modules/selectors/app'
import {
  updateSelfAccountErrorSelector,
  updateSelfAccountWasUpdatedSelector
} from '../../../../../modules/selectors/selfAccounts'

import { AD_APPROVERS_LIST } from '../../../../../constants/permissions'

const useGetMembersListActionOptions = ({ selfAccountName, selfAccountId, approvers, openDeactivateUserModal }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const permissions = usePermissions()

  // When we update selfAccount approvers field, we save userId to this local state, to show skeleton for current member
  const [memberIdApprover, setMemberIdApprover] = useState('')

  const { id: updatedMemberId, isLoading: isUpdatedMemberLoading } = useSelector(updatedMemberSelector)
  const currentUserId = useSelector(getCurrentUserIDSelector)
  const selfAccountWasUpdated = useSelector(updateSelfAccountWasUpdatedSelector)
  const updateSelfAccountError = useSelector(updateSelfAccountErrorSelector)

  const canManageAdApprovers = permissions.can('manage', AD_APPROVERS_LIST)

  const memberUpdatingId = useMemo(() => {
    if (isUpdatedMemberLoading) {
      return updatedMemberId
    } else if (memberIdApprover) {
      return memberIdApprover
    } else {
      return ''
    }
  }, [isUpdatedMemberLoading, updatedMemberId, memberIdApprover])

  const changeMemberStatusHandler = useCallback(
    ({ id, isActive }) => {
      if (isActive) {
        openDeactivateUserModal({ id })
      } else {
        dispatch(updateMemberStatus(id, 'inactive'))
      }
    },
    [dispatch, openDeactivateUserModal]
  )

  const changeMemberRoleHandler = useCallback(
    ({ id, role }) => {
      const updatedRole = role === 'admin' ? 'manager' : 'admin'

      dispatch(updateMemberRole(id, updatedRole))
    },
    [dispatch]
  )

  const setMemberAsAdApproverHandler = useCallback(
    member => {
      const { id, user: userId } = member
      let approversIds = approvers.map(item => item.id)

      const isUserInApprovers = isTeamMemberInApprovers(approvers, userId)

      if (isUserInApprovers) {
        approversIds = approversIds.filter(item => item !== userId)
      } else {
        approversIds.push(userId)
      }

      const updatedSelfAccountData = {
        name: selfAccountName,
        approvers: approversIds
      }

      setMemberIdApprover(id)

      dispatch(updateSelfAccount(selfAccountId, updatedSelfAccountData))
    },
    [dispatch, selfAccountName, approvers, selfAccountId, setMemberIdApprover]
  )

  const getMembersListActionDropdownOptions = useCallback(
    member => {
      const { role, is_active: isActive, id, user: userId } = member
      const isUserInApprovers = isTeamMemberInApprovers(approvers, userId)

      const isMe = userId === currentUserId

      return [
        ...insertIf(isActive && !isMe, {
          text: role === 'admin' ? t('Remove admin privileges') : t('Make admin'),
          onClickHandler: () => changeMemberRoleHandler(member)
        }),
        ...insertIf(!isMe, {
          text: isActive ? t('Deactivate user') : t('Activate user'),
          onClickHandler: () => changeMemberStatusHandler({ id, isActive })
        }),
        ...insertIf(canManageAdApprovers, {
          text: isUserInApprovers ? 'Remove ad approver' : 'Set as ad approver',
          onClickHandler: () => setMemberAsAdApproverHandler(member)
        })
      ]
    },
    [
      t,
      changeMemberRoleHandler,
      changeMemberStatusHandler,
      setMemberAsAdApproverHandler,
      approvers,
      canManageAdApprovers,
      currentUserId
    ]
  )

  useEffect(() => {
    if (selfAccountWasUpdated || updateSelfAccountError) {
      dispatch(clearUpdateSelfAccount())
      setMemberIdApprover('')
    }
  }, [dispatch, selfAccountWasUpdated, updateSelfAccountError])

  return { getMembersListActionDropdownOptions, memberUpdatingId }
}

export default useGetMembersListActionOptions
